import React, { useState } from 'react';
import { Calendar, CalendarRange, Filter, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/reporting/DateRangePicker';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { format } from 'date-fns';
import { PAYMENT_METHODS, PaymentMethod } from '@/types/constants';

export interface PaymentFiltersProps {
  onDateRangeChange: (range: { from: Date; to: Date } | undefined) => void;
  onStatusChange: (status: string | undefined) => void;
  onPaymentMethodChange: (paymentMethod: PaymentMethod | undefined) => void;
  dateRange: { from: Date; to: Date } | undefined;
  status: string | undefined;
  paymentMethod: PaymentMethod | undefined;
  isLoading: boolean;
  onResetFilters: () => void;
}

const STATUS_OPTIONS = [
  { value: undefined, label: 'All Statuses' },
  { value: 'paid', label: 'Paid' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' }
];

const PAYMENT_METHOD_OPTIONS = [
  { value: undefined, label: 'All Methods' },
  { value: 'stripe' as PaymentMethod, label: 'Credit Card' },
  { value: 'cash' as PaymentMethod, label: 'Cash' },
  { value: 'check' as PaymentMethod, label: 'Check' },
  { value: 'money_order' as PaymentMethod, label: 'Money Order' },
  { value: 'manual_card' as PaymentMethod, label: 'Manual Card' },
];

const PaymentFilters: React.FC<PaymentFiltersProps> = ({ 
  onDateRangeChange,
  onStatusChange,
  onPaymentMethodChange,
  dateRange,
  status,
  paymentMethod,
  isLoading,
  onResetFilters
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasActiveFilters = !!(dateRange || status || paymentMethod);
  
  const getStatusLabel = () => {
    const option = STATUS_OPTIONS.find(opt => opt.value === status);
    return option ? option.label : 'All Statuses';
  };

  const getPaymentMethodLabel = () => {
    const option = PAYMENT_METHOD_OPTIONS.find(opt => opt.value === paymentMethod);
    return option ? option.label : 'All Methods';
  };
  
  const getDateRangeLabel = () => {
    if (!dateRange) return 'All Time';
    
    return `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`;
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="bg-white p-4 rounded-md border mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">Payment Filters</h3>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={onResetFilters}
              disabled={isLoading}
            >
              Reset Filters
            </Button>
          )}
          <CollapsibleTrigger asChild>
            <Button variant="outline" size="sm">
              {isOpen ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>
      
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 mb-4">
          {dateRange && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {getDateRangeLabel()}
            </Badge>
          )}
          {status && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Filter className="h-3 w-3" />
              {getStatusLabel()}
            </Badge>
          )}
          {paymentMethod && (
            <Badge variant="outline" className="flex items-center gap-1">
              <CreditCard className="h-3 w-3" />
              {getPaymentMethodLabel()}
            </Badge>
          )}
        </div>
      )}
      
      <CollapsibleContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Date Range</label>
            <DateRangePicker
              className="w-full"
              value={dateRange}
              onChange={onDateRangeChange}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Payment Status</label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  {getStatusLabel()}
                  <Filter className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuGroup>
                  {STATUS_OPTIONS.map((option) => (
                    <DropdownMenuItem 
                      key={option.label}
                      onClick={() => onStatusChange(option.value)}
                      className={option.value === status ? "bg-muted" : ""}
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Payment Method</label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  {getPaymentMethodLabel()}
                  <CreditCard className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuGroup>
                  {PAYMENT_METHOD_OPTIONS.map((option) => (
                    <DropdownMenuItem 
                      key={option.label}
                      onClick={() => onPaymentMethodChange(option.value)}
                      className={option.value === paymentMethod ? "bg-muted" : ""}
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default PaymentFilters;